import {
    createAdminUser,
    isAdmin as isAdminType,
    updateUser as updateAdminUser,
    updateAppUser,
    useAdmin,
    useUser,
    useUserAdminForm,
} from '@gozoki/api';
import { RIGHTS, RIGHTS_LABELS } from '@gozoki/tools';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '../../components/communication/Toast';
import LoadingPaper from '../../components/containers/LoadingPaper';
import TextFieldRhf from '../../components/inputs/TextFieldRhf';
import Page from '../../components/Page';
import { ROUTES } from '../../utils/constants/routes.constants';
import { getErrorMessageWithSentry } from '../../utils/sentry';
import { ThemeColors } from '../../utils/theme/colors.theme';
import { fonts } from '../../utils/theme/fonts.theme';
import { ButtonOverrides } from '../../utils/theme/overrides.theme';

const AddUserPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();

    const updateMode = id !== undefined; // Update mode if there is a promo campaign ID in the URL
    const isAdmin = location.state?.isAdmin ?? false; // Determine if we're editing an admin or app user

    // If this is update mode, use isLoading to show a loading indicator before the data arrives
    const { data: user, isLoading: isLoadingUser } = useUser(parseInt(id ?? '', 10));
    const { data: admin, isLoading: isLoadingAdmin } = useAdmin(parseInt(id ?? '', 10));

    const userData = isAdmin ? admin : user;
    const isLoading = isAdmin ? isLoadingAdmin : isLoadingUser;

    const { control, trigger, watch, getValues, setValue } = useUserAdminForm();

    useEffect(() => {
        watch('adminRights');
    }, [watch]);

    const [Toast, showToast] = useToast();

    const [modifyPassword, setModifyPassword] = useState(!updateMode);
    const [openDialog, setOpenDialog] = useState(false);

    // Fill the form with initial data once the data is fetched
    useEffect(() => {
        // Set all values
        if (updateMode && userData) {
            setValue('firstName', userData.firstName);
            setValue('lastName', userData.lastName);
            setValue('email', userData.email);

            if (isAdminType(userData)) {
                setValue('adminRights', userData.adminRights);
            } else {
                setValue('adminRights', 0);
            }
        }
    }, [setValue, updateMode, userData, isAdmin]);

    const navigateBack = useCallback(() => {
        navigate(ROUTES.CLIENTS);
    }, [navigate]);

    // ******************************************************************** //
    //                              MUTATIONS                               //
    // ******************************************************************** //

    const { mutate: upload, isPending: isUploading } = useMutation({
        mutationFn: createAdminUser,
        onSuccess: navigateBack,
        onError: (error) => {
            showToast({
                severity: 'error',
                message: getErrorMessageWithSentry(error),
            });
        },
    });

    const { mutate: updateAdmin, isPending: isUpdatingAdmin } = useMutation({
        mutationFn: updateAdminUser,
        onSuccess: navigateBack,
        onError: (error) => {
            showToast({
                severity: 'error',
                message: getErrorMessageWithSentry(error),
            });
        },
    });

    const { mutate: updateApp, isPending: isUpdatingApp } = useMutation({
        mutationFn: updateAppUser,
        onSuccess: navigateBack,
        onError: (error) => {
            showToast({
                severity: 'error',
                message: getErrorMessageWithSentry(error),
            });
        },
    });

    const isAdminUser = isAdmin || !updateMode; // For creation, default to admin; for update, use context

    // ******************************************************************** //
    //                               HANDLERS                               //
    // ******************************************************************** //

    const handleCreateUser = async () => {
        const valid = await trigger(); // Trigger form validation

        if (valid) {
            upload(getValues());
        } else {
            showToast({
                severity: 'error',
                message: 'Veuillez remplir tous les champs obligatoires',
            });
        }
    };

    const handleUpdateUser = async () => {
        const valid = await trigger(); // Trigger form validation

        if (valid) {
            if (isAdminUser) {
                updateAdmin({ userId: userData?.id ?? 0, ...getValues() });
            } else {
                const values = getValues();
                updateApp({ userId: userData?.id ?? 0, ...values });
            }
        } else {
            showToast({
                severity: 'error',
                message: 'Veuillez remplir tous les champs obligatoires',
            });
        }
    };

    const rightsOptions = Object.keys(RIGHTS)
        .slice(2)
        .map((key) => ({
            value: RIGHTS[key as keyof typeof RIGHTS],
            label: RIGHTS_LABELS[key as keyof typeof RIGHTS],
        }));

    const toggleRight = (right: number) => {
        if (getValues('adminRights') & RIGHTS.SUPER_ADMIN) return; // Can't remove super admin rights
        const newRights = getValues('adminRights') ^ right;
        setValue('adminRights', newRights);
    };

    return (
        <>
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir rendre cet utilisateur super admin ?
                    </DialogContentText>
                    <br />
                    <DialogContentText>
                        Les super administrateurs ont tous les droits sur le back-office et sur les
                        autres utilisateurs. Ce rôle doit être limité à un nombre restreint
                        d'utilisateurs. Etre super administrateur ne change rien à l'utilisation du
                        back-office, donner tous les autres droits à un utilisateur suffit pour
                        qu'il puisse tout faire.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="warning"
                        onClick={() => {
                            setValue('adminRights', 1);
                            setOpenDialog(false);
                        }}
                    >
                        Confirmer
                    </Button>
                    <Button
                        autoFocus
                        variant="contained"
                        color="success"
                        onClick={() => setOpenDialog(false)}
                    >
                        Annuler
                    </Button>
                </DialogActions>
            </Dialog>
            <Page sx={{ maxWidth: '50vw', margin: 'auto' }}>
                <Toast />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={navigateBack}>
                        <ArrowBackIcon
                            sx={{
                                color: ThemeColors.BLACK,
                                height: '24px',
                                width: '24px',
                            }}
                        />
                    </IconButton>
                    <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
                        {updateMode ? 'Modifier un utilisateur' : 'Ajouter un utilisateur'}
                    </Typography>

                    <Button
                        variant="outlined"
                        sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
                        onClick={navigateBack}
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        disabled={isUpdatingAdmin || isUpdatingApp || isUploading}
                        onClick={updateMode ? handleUpdateUser : handleCreateUser}
                    >
                        {updateMode ? 'Modifier' : 'Ajouter'}
                    </Button>
                </div>
                <LoadingPaper
                    sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
                    loading={isLoading && updateMode}
                >
                    <Typography mb="8px" style={fonts.inputHeader}>
                        Prénom
                    </Typography>
                    <TextFieldRhf
                        variant="outlined"
                        placeholder="Prénom"
                        color="info"
                        control={control}
                        trigger={trigger}
                        name="firstName"
                        fullWidth
                    />
                    <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                        Nom
                    </Typography>
                    <TextFieldRhf
                        variant="outlined"
                        placeholder="Nom"
                        color="info"
                        control={control}
                        trigger={trigger}
                        name="lastName"
                        fullWidth
                    />

                    <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                        Email
                    </Typography>
                    <TextFieldRhf
                        variant="outlined"
                        placeholder="Email"
                        color="info"
                        control={control}
                        trigger={trigger}
                        name="email"
                        fullWidth
                    />

                    <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                        Mot de passe
                    </Typography>
                    {modifyPassword ? (
                        <TextFieldRhf
                            variant="outlined"
                            placeholder="Mot de passe"
                            color="info"
                            control={control}
                            trigger={trigger}
                            name="password"
                            fullWidth
                            validateRealTime
                        />
                    ) : (
                        <Button onClick={() => setModifyPassword(true)}>
                            Modifier le mot de passe
                        </Button>
                    )}
                    {isAdminUser && (
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <Typography
                                mb="8px"
                                mt="24px"
                                style={{ ...fonts.inputHeader, flex: 1 }}
                            >
                                Droits d'administration
                            </Typography>
                            <Button
                                color="warning"
                                onClick={() => setOpenDialog(true)}
                                disabled={getValues('adminRights') === 1}
                                style={{ flex: 1 }}
                            >
                                {getValues('adminRights') === 1
                                    ? 'Utilisateur super administrateur'
                                    : 'Passer super administrateur'}
                            </Button>
                        </div>
                    )}
                    {isAdminUser && (
                        <div>
                            {rightsOptions.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    control={
                                        <Checkbox
                                            checked={
                                                (getValues('adminRights') & option.value) !== 0 ||
                                                getValues('adminRights') === 1
                                            }
                                            onChange={() => toggleRight(option.value)}
                                            value={option.value}
                                        />
                                    }
                                    label={option.label}
                                />
                            ))}
                        </div>
                    )}
                </LoadingPaper>
            </Page>
        </>
    );
};

export default AddUserPage;
